// Materials: fh,k (ETA), wood-based panels (EC5), Tipo/Specie/Classe option lists and database defaults, kmod/gammaM.

import { cos, sin } from "./angles";
import type { EngineCatalog, ScrewRecord, TimberClassProps } from "./catalog";
import { isPanelMat, isSteelMat, type EngineCtx } from "./ctx";
import { GAMMA_M_CATEGORIA_FISSA } from "./data";
import type { ConnectionType } from "./types";

export type ElementN = 1 | 2;

export function etaFhK(d: number, rhok: number, alphaDeg: number, predrilled: boolean): number {
  const rk = Math.min(rhok, 590);
  const a = Math.abs(alphaDeg);
  const denom = 2.5 * cos(a) * cos(a) + sin(a) * sin(a);
  return predrilled ? (0.082 * rk * (1 - 0.01 * d)) / denom : (0.082 * rk * Math.pow(d, -0.3)) / denom;
}

/* PANEL-TIMBER: element 1 = wood-based panel (head side), element 2 = timber. ETA-11/0030 admits panels only on the
   head side and gives neither distances nor embedment for panels: EC5 §8.3.1.2-8.3.1.3 and §8.5.1.2 are used. */
export const PANEL_TIPO = "Pannelli a base di legno";
export const PANEL_EXCLUDED_CLASSI = ["OSB-2", "Magnumboard ®"]; // OSB/2 not admitted by the ETA; Magnum Board: no EC5 fh,k
export const PANEL_MIN_T: Record<string, number> = { plywood: 6, fibre: 6, particle: 8, osb: 8 }; // ETA-11/0030 p. 13-14 [mm]

export type PanelKind = "plywood" | "osb" | "particle" | "fibre";

export function panelKindOf(specie: string, classe: string): PanelKind | null {
  const t = (specie || "") + " " + (classe || "");
  if (/compensat|plywood|EN ?636/i.test(t)) return "plywood";
  if (/OSB/i.test(t)) return "osb";
  if (/particell/i.test(t)) return "particle";
  if (/fibr|MDF|HB\./i.test(t)) return "fibre";
  return null;
}
export function panelClassiAmmesse(cat: EngineCatalog, tipo: string, specie: string): string[] {
  return Object.keys(cat.TIMBER_DB[tipo]?.[specie] ?? {}).filter((c) => !PANEL_EXCLUDED_CLASSI.includes(c));
}
// Panel embedment, EC5 §8.3.1.3(3) eq. (8.20)-(8.22) for d <= 8 mm (head >= 2d); for d > 8 mm bolt values §8.5.1.2
// eq. (8.36)-(8.37).
export function panelFhK(kind: PanelKind | null, d: number, rhok: number, t: number): { value: number; eq: string } | null {
  if (!kind || !Number.isFinite(d) || !Number.isFinite(t)) return null;
  if (d <= 8) {
    if (kind === "plywood") return Number.isFinite(rhok) ? { value: 0.11 * rhok * Math.pow(d, -0.3), eq: "8.20" } : null;
    if (kind === "fibre") return { value: 30 * Math.pow(d, -0.3) * Math.pow(t, 0.6), eq: "8.21" };
    return { value: 65 * Math.pow(d, -0.7) * Math.pow(t, 0.1), eq: "8.22" };
  }
  if (kind === "plywood") return Number.isFinite(rhok) ? { value: 0.11 * (1 - 0.01 * d) * rhok, eq: "8.36" } : null;
  if (kind === "osb" || kind === "particle") return { value: 50 * Math.pow(d, -0.6) * Math.pow(t, 0.2), eq: "8.37" };
  return null;
}
// Panel kmod, EC5 Table 3.1; null when the service class is not admitted for that panel.
export function panelKmod(classe: string, sc: number, durata: string): number | null {
  const i = ({ permanente: 0, lungaDurata: 1, medioTermine: 2, breveTermine: 3, istantanea: 4 } as Record<string, number>)[durata];
  if (i == null) return null;
  const c = classe || "";
  const A = [0.6, 0.7, 0.8, 0.9, 1.1],
    B = [0.5, 0.55, 0.65, 0.7, 0.9];
  const OSB3: Record<number, number[]> = { 1: [0.4, 0.5, 0.7, 0.9, 1.1], 2: [0.3, 0.4, 0.55, 0.7, 0.9] };
  const P45 = [0.3, 0.45, 0.65, 0.85, 1.1],
    P67 = [0.4, 0.5, 0.7, 0.9, 1.1]; // service class 1
  let row: number[] | null = null;
  let m: RegExpExecArray | null;
  if ((m = /EN ?636-?([123])/i.exec(c))) {
    const ty = Number(m[1] ?? 0);
    if (sc <= ty) row = sc === 3 ? B : A;
  } else if (/OSB\/?[34]/i.test(c)) row = OSB3[sc] ?? null;
  else if (/OSB\/?2/i.test(c)) row = sc === 1 ? [0.3, 0.45, 0.65, 0.85, 1.1] : null;
  else if ((m = /P([4-7])\b/.exec(c))) {
    const ty = Number(m[1] ?? 0);
    if (sc === 1) row = ty <= 5 ? P45 : P67;
    else if (sc === 2) row = ty === 5 ? [0.2, 0.3, 0.45, 0.6, 0.8] : ty === 7 ? [0.3, 0.4, 0.55, 0.7, 0.9] : null;
  }
  return row ? (row[i] ?? null) : null;
}
// Screw admitted in the panel-timber module: only HBS and TBS families, "Panels" support compatible, head d_k >= 2·d.
export function panelScrewOk(ctx: EngineCtx, s: ScrewRecord): boolean {
  if (!isPanelMat(ctx.v.materiale1)) return true;
  const g = s.geometria_mm;
  return (
    /^(HBS|TBS)\b/i.test(s.family || "") &&
    !!s.supportiCompatibiliEU &&
    s.supportiCompatibiliEU["Panels"] === 2 &&
    Number.isFinite(g.dk) &&
    Number.isFinite(g.d1) &&
    g.dk >= 2 * g.d1 - 1e-9
  );
}

// Particleboard (EN 312, P4–P7) ρk by thickness, EN 12369-1 (tMax = upper bound of the band, included). The DB value
// (500, minimum over all thicknesses) stays as fallback when the thickness is not entered.
const PARTICLEBOARD_RHOK_BANDS = [
  { tMax: 13, rhok: 650 },
  { tMax: 20, rhok: 600 },
  { tMax: 25, rhok: 550 },
  { tMax: 32, rhok: 550 },
  { tMax: 40, rhok: 500 },
  { tMax: Infinity, rhok: 500 },
];
export const PARTICLEBOARD_CLASSI = [
  "Pannello particelle P4",
  "Pannello particelle P5",
  "Pannello particelle P6",
  "Pannello particelle P7",
];
export function panelRhokBySpessore(classe: string, t: number): number | null {
  if (!PARTICLEBOARD_CLASSI.includes(classe) || !(t > 0)) return null;
  return PARTICLEBOARD_RHOK_BANDS.find((b) => t <= b.tMax)!.rhok;
}

// "Tipo di materiale" options. Element 2 is always timber (no steel, no panel). legno3d/pannello3d: no CLT (user
// request 2026-10-14). legno3: CLT-timber-CLT and timber-CLT-timber are admitted, never three CLT → CLT can't be on
// element 1 and on 2/3 together. Element 1: panel types → only panels; steel → only "Acciaio"; else all the others.
export function matTipiFor(cat: EngineCatalog, materiale1: ConnectionType | string, n: ElementN, otherTipo: string): string[] {
  let noClt = materiale1 === "legno3d" || materiale1 === "pannello3d";
  if (materiale1 === "legno3" && otherTipo === "Pannelli CLT") noClt = true;
  const tipi = Object.keys(cat.TIMBER_DB);
  if (n === 2) return tipi.filter((t) => t !== "Acciaio" && t !== PANEL_TIPO && !(noClt && t === "Pannelli CLT"));
  if (isPanelMat(materiale1)) return tipi.filter((t) => t === PANEL_TIPO);
  const isSteel = isSteelMat(materiale1);
  return tipi.filter((t) => (t === "Acciaio") === isSteel && t !== PANEL_TIPO && !(noClt && t === "Pannelli CLT"));
}
export function matSpecieFor(cat: EngineCatalog, materiale1: ConnectionType | string, n: ElementN, tipo: string): string[] {
  return Object.keys(cat.TIMBER_DB[tipo] ?? {}).filter(
    (s) => !(n === 1 && isPanelMat(materiale1)) || panelClassiAmmesse(cat, tipo, s).length > 0,
  );
}
export function matClasseFor(
  cat: EngineCatalog,
  materiale1: ConnectionType | string,
  n: ElementN,
  tipo: string,
  specie: string,
): string[] {
  return Object.keys(cat.TIMBER_DB[tipo]?.[specie] ?? {}).filter(
    (c) => !(n === 1 && isPanelMat(materiale1)) || !PANEL_EXCLUDED_CLASSI.includes(c),
  );
}

// USER 1..5 slots: single fixed class, specie/classe selects replaced by a free name (label only).
export function isUserTipo(tipo: string): boolean {
  return /^USER\b/.test(tipo || "");
}
export function updateMatSpecieVisibility(ctx: EngineCtx, n: ElementN): void {
  const tipo = n === 1 ? ctx.v.mat1Tipo : ctx.v.mat2Tipo;
  if (n === 1) ctx.ui.visible.mat1SpecieField = tipo !== "Acciaio";
  const isUser = isUserTipo(tipo);
  ctx.ui.visible[`mat${n}SpecieClasseRow` as const] = !isUser;
  ctx.ui.visible[`mat${n}CustomNameField` as const] = isUser;
}

// populateMatTipo/populateMatSpecie/populateMatClasse: option lists, keeping the previous value when still valid,
// otherwise the first option ('' when there are none).
const pick = (list: string[], prev: string): string => (list.includes(prev) ? prev : (list[0] ?? ""));
export function populateMatTipo(ctx: EngineCtx, n: ElementN): void {
  const list = matTipiFor(ctx.cat, ctx.v.materiale1, n, n === 2 ? ctx.v.mat1Tipo : ctx.v.mat2Tipo);
  ctx.options.matTipo[n] = list;
  ctx.v[`mat${n}Tipo` as const] = pick(list, ctx.v[`mat${n}Tipo` as const]);
}
export function populateMatSpecie(ctx: EngineCtx, n: ElementN): void {
  const list = matSpecieFor(ctx.cat, ctx.v.materiale1, n, ctx.v[`mat${n}Tipo` as const]);
  ctx.options.matSpecie[n] = list;
  ctx.v[`mat${n}Specie` as const] = pick(list, ctx.v[`mat${n}Specie` as const]);
}
export function populateMatClasse(ctx: EngineCtx, n: ElementN): void {
  const list = matClasseFor(ctx.cat, ctx.v.materiale1, n, ctx.v[`mat${n}Tipo` as const], ctx.v[`mat${n}Specie` as const]);
  ctx.options.matClasse[n] = list;
  ctx.v[`mat${n}Classe` as const] = pick(list, ctx.v[`mat${n}Classe` as const]);
}

export function timberProps(cat: EngineCatalog, tipo: string, specie: string, classe: string): TimberClassProps | undefined {
  return cat.TIMBER_DB[tipo]?.[specie]?.[classe];
}

export type MaterialDefaults = {
  rhok: number | null; // null = no DB value (field left as is, "ρk N.D." warning)
  rhomean: number | null; // null = no DB value (field emptied, warning)
};
// Database values applied when a material class is chosen (applyMatClasse): ρk (particleboard: by thickness t),
// ρmean. Both fields stay editable.
export function materialDefaults(
  cat: EngineCatalog,
  tipo: string,
  specie: string,
  classe: string,
  thickness: number,
): MaterialDefaults | null {
  const props = timberProps(cat, tipo, specie, classe);
  if (!props) return null;
  let rhok: number | null = null;
  if (props.rhok != null) {
    const rhokSp = panelRhokBySpessore(classe, thickness);
    rhok = rhokSp != null ? rhokSp : props.rhok;
  }
  return { rhok, rhomean: props.rhomean != null ? props.rhomean : null };
}

// applyMatClasse(n): writes the DB densities into the controls. When ρk is missing the field keeps its value (and
// a warning is shown); when ρmean is missing the field is emptied (no leftover from another material: K_ser N.D.).
export function applyMatClasse(ctx: EngineCtx, n: ElementN): void {
  const v = ctx.v;
  const tipo = v[`mat${n}Tipo` as const],
    specie = v[`mat${n}Specie` as const],
    classe = v[`mat${n}Classe` as const];
  const def = materialDefaults(ctx.cat, tipo, specie, classe, v[`spessore${n}` as const]);
  if (!def) return;
  if (def.rhok != null) v[`rhok${n}` as const] = def.rhok;
  v[`rhomean${n}` as const] = def.rhomean != null ? def.rhomean : NaN;
  updateMatWarnings(ctx, n);
}
// Warnings of applyMatClasse, derived from the selected class.
export function updateMatWarnings(ctx: EngineCtx, n: ElementN): void {
  const v = ctx.v;
  const props = timberProps(ctx.cat, v[`mat${n}Tipo` as const], v[`mat${n}Specie` as const], v[`mat${n}Classe` as const]);
  if (!props) return;
  ctx.ui.visible[`mat${n}Warn` as const] = props.rhok == null;
  ctx.ui.visible[`rhomean${n}Warn` as const] = props.rhomean == null;
}

// gammaM of a connection: "Connessioni" (EC5, single value) / "Unioni" (NTC2018, column A/B).
export function gammaMValue(cat: EngineCatalog, normativa: string, colonna: string): number {
  if (normativa === "EC5") return cat.GAMMA_M_EC5[GAMMA_M_CATEGORIA_FISSA["EC5"] ?? ""]?.val ?? NaN;
  const row = cat.GAMMA_M_NTC2018[GAMMA_M_CATEGORIA_FISSA["NTC2018"] ?? ""];
  if (!row) return NaN;
  return colonna === "B" ? row.B : row.A;
}
